import { boolean, lazy, mixed, object, string } from 'yup';
const REQUIRED_MESSAGE = 'Please select an option';
const DemographicInfoSchema = object({
  ethnicity: lazy(values => {
    const {
      hispanicOrLatino,
      notHispanicOrLatino,
      noInformationProvided,
      hispanicOrLatinoList
    } = values;
    if (!hispanicOrLatino && !notHispanicOrLatino && !noInformationProvided && !hispanicOrLatinoList.mexican && !hispanicOrLatinoList.puertoRican && !hispanicOrLatinoList.cuban && !hispanicOrLatinoList.enteredOrigin) {
      return object().shape({
        hispanicOrLatino: boolean().oneOf([true], REQUIRED_MESSAGE),
        notHispanicOrLatino: boolean().oneOf([true], REQUIRED_MESSAGE),
        noInformationProvided: boolean().oneOf([true], REQUIRED_MESSAGE),
        hispanicOrLatinoList: lazy(values => {
          const {
            mexican,
            puertoRican,
            cuban,
            other
          } = values;
          if (!mexican && !puertoRican && !cuban && !other) {
            return object().shape({
              mexican: boolean().oneOf([true], REQUIRED_MESSAGE),
              puertoRican: boolean().oneOf([true], REQUIRED_MESSAGE),
              cuban: boolean().oneOf([true], REQUIRED_MESSAGE),
              other: boolean().oneOf([true], REQUIRED_MESSAGE)
            });
          } else if (other) {
            return object().shape({
              enteredOrigin: string().required('Please provide an origin')
            });
          }
          return mixed().notRequired();
        })
      });
    }
    if (hispanicOrLatinoList.other) {
      return object().shape({
        hispanicOrLatinoList: lazy(values => {
          const {
            mexican,
            puertoRican,
            cuban,
            other
          } = values;
          if (!mexican && !puertoRican && !cuban && !other) {
            return object().shape({
              mexican: boolean().oneOf([true], REQUIRED_MESSAGE),
              puertoRican: boolean().oneOf([true], REQUIRED_MESSAGE),
              cuban: boolean().oneOf([true], REQUIRED_MESSAGE),
              other: boolean().oneOf([true], REQUIRED_MESSAGE)
            });
          } else if (other) {
            return object().shape({
              enteredOrigin: string().required('Please provide an origin')
            });
          }
          return mixed().notRequired();
        })
      });
    }
    return mixed().notRequired();
  }),
  race: lazy(values => {
    const {
      americanIndianOrAlaskaNative,
      asian,
      blackOrAfricanAmerican,
      nativeHawaiianOrOtherPacificIslander,
      white,
      noInformationProvided,
      asianList,
      nativeHawaiianOrOtherPacificIslanderList
    } = values;
    if (!americanIndianOrAlaskaNative && !asian && !blackOrAfricanAmerican && !nativeHawaiianOrOtherPacificIslander && !white && !noInformationProvided && !asianList.asianIndian && !asianList.chinese && !asianList.filipino && !asianList.korean && !asianList.vietnamese && !asianList.japanese && !asianList.enteredRace && !nativeHawaiianOrOtherPacificIslanderList.nativeHawaiian && !nativeHawaiianOrOtherPacificIslanderList.guamanianOrChamorro && !nativeHawaiianOrOtherPacificIslanderList.samoan && !nativeHawaiianOrOtherPacificIslanderList.enteredRace) {
      return object().shape({
        americanIndianOrAlaskaNative: boolean().oneOf([true], REQUIRED_MESSAGE),
        asian: boolean().oneOf([true], REQUIRED_MESSAGE),
        blackOrAfricanAmerican: boolean().oneOf([true], REQUIRED_MESSAGE),
        nativeHawaiianOrOtherPacificIslander: boolean().oneOf([true], REQUIRED_MESSAGE),
        white: boolean().oneOf([true], REQUIRED_MESSAGE),
        noInformationProvided: boolean().oneOf([true], REQUIRED_MESSAGE),
        asianList: lazy(values => {
          const {
            asianIndian,
            chinese,
            filipino,
            korean,
            vietnamese,
            japanese,
            other
          } = values;
          if (!asianIndian && !chinese && !filipino && !korean && !vietnamese && !japanese && !other) {
            return object().shape({
              asianIndian: boolean().oneOf([true], REQUIRED_MESSAGE),
              chinese: boolean().oneOf([true], REQUIRED_MESSAGE),
              filipino: boolean().oneOf([true], REQUIRED_MESSAGE),
              korean: boolean().oneOf([true], REQUIRED_MESSAGE),
              vietnamese: boolean().oneOf([true], REQUIRED_MESSAGE),
              japanese: boolean().oneOf([true], REQUIRED_MESSAGE),
              other: boolean().oneOf([true], REQUIRED_MESSAGE)
            });
          } else if (other) {
            return object().shape({
              enteredRace: string().required('Please provide race')
            });
          }
          return mixed().notRequired();
        }),
        nativeHawaiianOrOtherPacificIslanderList: lazy(values => {
          const {
            nativeHawaiian,
            guamanianOrChamorro,
            samoan,
            other
          } = values;
          if (!nativeHawaiian && !guamanianOrChamorro && !samoan && !other) {
            return object().shape({
              nativeHawaiian: boolean().oneOf([true], REQUIRED_MESSAGE),
              guamanianOrChamorro: boolean().oneOf([true], REQUIRED_MESSAGE),
              samoan: boolean().oneOf([true], REQUIRED_MESSAGE),
              other: boolean().oneOf([true], REQUIRED_MESSAGE)
            });
          } else if (other) {
            return object().shape({
              enteredRace: string().required('Please provide race')
            });
          }
          return mixed().notRequired();
        })
      });
    }
    if (nativeHawaiianOrOtherPacificIslanderList.other || asianList.other || americanIndianOrAlaskaNative) {
      return object().shape({
        americanIndianOrAlaskaNativeName: string().required('Please provide name'),
        nativeHawaiianOrOtherPacificIslanderList: lazy(values => {
          const {
            other
          } = values;
          if (other) {
            return object().shape({
              enteredRace: string().required('Please provide race')
            });
          }
          return mixed().notRequired();
        }),
        asianList: lazy(values => {
          const {
            other
          } = values;
          if (other) {
            return object().shape({
              enteredRace: string().required('Please provide race')
            });
          }
          return mixed().notRequired();
        })
      });
    }
    return mixed().notRequired();
  }),
  sex: object().shape({
    male: boolean().nullable().required(REQUIRED_MESSAGE).when(['female', 'noInformationProvided'], {
      is: (female, noInformationProvided) => !noInformationProvided && !female,
      then: schema => schema.oneOf([true], REQUIRED_MESSAGE)
    }),
    female: boolean().nullable().when(['male', 'noInformationProvided'], {
      is: (male, noInformationProvided) => !noInformationProvided && !male,
      then: schema => schema.oneOf([true], REQUIRED_MESSAGE)
    }),
    noInformationProvided: boolean().nullable().when(['female', 'male'], {
      is: (female, male) => !male && !female,
      then: schema => schema.oneOf([true], REQUIRED_MESSAGE)
    })
  }, [['female', 'noInformationProvided'], ['male', 'noInformationProvided'], ['female', 'male']])
});
export default DemographicInfoSchema;
//# sourceMappingURL=index.js.map